"use client";

import { useCallback, useState } from "react";
import type { PageView } from "@/types/take";
import type { DemoView } from "@/types/demo";
import { useFullscreenAntiCheat } from "@/hooks/use-fullscreen-anti-cheat";

/**
 * useExamSecurity — owns the fullscreen + anti-cheat cluster of the attempt page.
 *
 * Thin wrapper (F9.7) over `useFullscreenAntiCheat` — the behavior itself lives
 * there, VERBATIM from the original hook. This wrapper wires the -10s
 * tab-switch penalty to the shared timer array via `setTimers` and bumps the
 * PAGE's violation counter via `setTabSwitchCount` (the page seeds/restores it
 * from cache/backend, so the core must not own that number), and enables the
 * screen-record permission check the demo does not run.
 *
 * NOTE: Fullscreen API + screen-capture permission cannot be exercised under
 * jsdom, so this hook has no unit test; verification is deferred to the manual
 * smoke test.
 */
export function useExamSecurity(opts: {
  view: PageView;
  sectionIdx: number;
  durationMode: "global" | "per_section";
  setTimers: React.Dispatch<React.SetStateAction<number[]>>;
  setTabSwitchCount: React.Dispatch<React.SetStateAction<number>>;
}) {
  const { view, sectionIdx, durationMode, setTimers, setTabSwitchCount } = opts;

  return useFullscreenAntiCheat({
    view,
    sectionIdx,
    durationMode,
    setTimers,
    detectScreenCapture: true,
    onPenalty: () => setTabSwitchCount((c) => c + 1),
  });
}

/**
 * useDemoExamSecurity — varian untuk demo exam (`/coba`).
 *
 * Delta dari real exam (kenapa dibedakan): hook MEMILIKI `tabSwitchCount`
 * state internal (real exam menyimpannya di page/cache), timer tunggal demo
 * diadaptasi ke kontrak array core (`timers[0]` = timer demo, penalti -10s
 * mendarat di situ), dan izin screen-record dimatikan.
 */
export function useDemoExamSecurity(opts: {
  view: DemoView;
  setTimer: React.Dispatch<React.SetStateAction<number>>;
}) {
  const { view, setTimer } = opts;

  const [tabSwitchCount, setTabSwitchCount] = useState(0);

  // Adapt the demo's single-number timer to the core's timer-array contract:
  // `timers[0]` IS the demo timer, so the -10s penalty lands on it unchanged.
  const setTimerAsArray = useCallback(
    (value: React.SetStateAction<number[]>) => {
      setTimer((prev) => {
        const next = typeof value === "function" ? value([prev]) : value;
        return next[0] ?? 0;
      });
    },
    [setTimer],
  );

  const core = useFullscreenAntiCheat({
    view,
    setTimers: setTimerAsArray,
    onPenalty: () => setTabSwitchCount((c) => c + 1),
  });

  return {
    requestFullscreen: core.requestFullscreen,
    exitFullscreen: core.exitFullscreen,
    tabSwitchCount,
    setTabSwitchCount,
    penaltyFlash: core.penaltyFlash,
    penaltyMessage: core.penaltyMessage,
    tabWarning: core.tabWarning,
    setTabWarning: core.setTabWarning,
    showFullscreenWarning: core.showFullscreenWarning,
    setShowFullscreenWarning: core.setShowFullscreenWarning,
    isWindowBlurred: core.isWindowBlurred,
  };
}
